import React, { useRef } from 'react';
import { Typography, Grid } from '@mui/material';
import { formatDate, formatMoney } from '../../../../../../lib/functions/utils';
import EnhancedTable from '../../../../../../lib/designSystem/Table';
import { boletinesLabels } from '../CompanyReport';
import { generatePersonReportPDF } from './components/exportPerson';
import { LightTooltip } from '../../../../../../lib/designSystem/WhiteTooltip';
import { CustomTooltip } from '../../../../../../lib/designSystem/TooltipTable';
import { Info } from '@mui/icons-material';
import html2canvas from 'html2canvas';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from 'recharts';

interface Props {
  response: any;
  lastUpdate?: Date;
}

const PersonReport: React.FC<Props> = ({ response, lastUpdate }) => {
  const data = response.data;
  const boletines = ['boled', 'bolcom', 'bolab', 'icom'].map((key) => ({
    key,
    label: key,
    data: data.debts[key],
  }));

  const chartRef = useRef<HTMLDivElement>(null);

  const chartData =
    data.patrimony?.riskScore?.historicalRate
      .map((item: any) => ({
        name: item.name.replace(/-/g, '/'),
        value: item.value,
      }))
      .reverse() || []; // Reverse to show oldest to newest

  const handleExport = async () => {
    const element = chartRef.current;
    if (!element) return;

    const canvas = await html2canvas(element, {
      backgroundColor: null,
      useCORS: true,
    });
    const imgData = chartData.length > 0 ? canvas.toDataURL('image/png') : null;
    generatePersonReportPDF(response, lastUpdate, imgData);
  };

  return (
    <div>
      <div>
        <div
          ref={chartRef}
          style={{
            width: 800,
            height: 200,
            position: 'absolute',
            left: -9999,
          }}
        >
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid stroke="#ccc" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Line type="monotone" dataKey="value" stroke="#0F23DF" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <Typography variant="h6" gutterBottom fontWeight="bold">
          Informe Buro Persona
        </Typography>
        <Typography
          variant="body2"
          style={{ cursor: 'pointer', color: 'blue', marginLeft: '10px' }}
          onClick={handleExport}
        >
          Exportar
        </Typography>
      </div>
      <div>
        <Typography variant="h6" fontWeight="bold">
          Resumen de Burós
        </Typography>
        <Grid container spacing={2} paddingBottom={2}>
          <Grid item xs={3}>
            <Typography variant="body1" fontWeight="bold">
              Últimos 6 meses
            </Typography>
            <Typography>
              {formatMoney(data.debts.summary.docsAmountLast6)}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="body1" fontWeight="bold">
              Últimos 12 meses
            </Typography>
            <Typography>
              {formatMoney(data.debts.summary.docsAmountLast12)}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="body1" fontWeight="bold">
              Últimos 24 meses
            </Typography>
            <Typography>
              {formatMoney(data.debts.summary.docsAmountLast24)}
            </Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography variant="body1" fontWeight="bold">
              Más de 24 meses
            </Typography>
            <Typography>
              {formatMoney(data.debts.summary.docsAmountMoreThan24)}
            </Typography>
          </Grid>
        </Grid>
      </div>
      <Grid container spacing={2}>
        {/* Datos Personales */}
        <Grid item xs={6}>
          <Typography variant="h6" fontWeight="bold">
            Datos Personales
          </Typography>
          <Typography>
            <strong>Nombre:</strong> {data.identificationDetails.name}
          </Typography>
          <Typography>
            <strong>Edad:</strong> {data.identificationDetails.age}
          </Typography>
          <Typography>
            <strong>Género:</strong> {data.identificationDetails.Gender}
          </Typography>
          <Typography>
            <strong>Fecha Nacimiento:</strong>{' '}
            {formatDate(data.identificationDetails.birthDate)}
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="h6" fontWeight="bold">
            Información Complementaria
          </Typography>
          <Typography>
            <strong>Email:</strong> {data.identificationDetails.emails}
          </Typography>
          <Typography>
            <strong>Profesión:</strong>{' '}
            {data.identificationDetails.profession || 'N/A'}
          </Typography>
          <Typography>
            <strong>Nacionalidad:</strong>{' '}
            {data.identificationDetails.nationality}
          </Typography>
          <Typography>
            <strong>Estado Civil:</strong>{' '}
            {data.identificationDetails.maritalStatus}
          </Typography>
        </Grid>

        {/* Patrimonio y económico */}
        <Grid item xs={6}>
          <Typography variant="h6" fontWeight="bold">
            Situación Económica
          </Typography>
          <Typography>
            <strong>Ingreso:</strong>{' '}
            {formatMoney(data.identificationDetails.salary)}
          </Typography>
          <Typography>
            <strong>Fonasa:</strong>{' '}
            {data.other?.IsFonasa === '1' ? 'Sí' : 'No'}
          </Typography>
          <Typography>
            <strong>GSE:</strong> {data.patrimony?.gse}
          </Typography>
          <Typography>
            <strong>Cod. ISE:</strong>{' '}
            {data.identificationDetails.other?.CodIse}
          </Typography>
          <Typography>
            <strong>Fecha Actualización ISE:</strong>{' '}
            {formatDate(data.identificationDetails.other?.DateUpdateIse)}
          </Typography>
          <Typography>
            <strong>Observaciones SII:</strong> {data.tributary?.observations}
          </Typography>
          <Typography>
            <strong>Boletín Concursal:</strong>{' '}
            {data.patrimony?.boletinConcursal == '0' ? 'No' : 'Sí'}
          </Typography>
        </Grid>

        {/* Bienes */}
        <Grid item xs={6}>
          <Typography variant="h6" fontWeight="bold">
            Garantías
          </Typography>
          <Typography>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Typography>
                <strong>Score de Riesgo:</strong>{' '}
                {data.patrimony?.riskScore?.score}
              </Typography>
              <LightTooltip
                title={CustomTooltip(
                  data.patrimony?.riskScore?.historicalRate,
                  'Fecha',
                  'Score',
                )}
                placement="left"
              >
                <Typography
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                  }}
                >
                  Ver Histórico{' '}
                  <Info style={{ marginLeft: '4px', fontSize: '14px' }} />
                </Typography>
              </LightTooltip>
            </div>
          </Typography>
          <Typography>
            <strong>Propiedades:</strong> {data.patrimony.realStateNumber}
          </Typography>
          <Typography>
            <strong>Valor Propiedades:</strong>{' '}
            {formatMoney(data.patrimony.realStateValuation)}
          </Typography>
          <Typography>
            <strong>Vehículos:</strong> {data.patrimony.vehiclesNumber}
          </Typography>
          <Typography>
            <strong>Valor Vehículos:</strong>{' '}
            {formatMoney(data.patrimony.vehiclesValuation)}
          </Typography>
          <Typography>
            <strong>Cuentas Bancarias:</strong>{' '}
            {data.patrimony?.bankAccounts?.length || 0}
          </Typography>
          <Typography>
            <strong>Sociedades:</strong>{' '}
            {data.patrimony?.societies?.length || 0}
          </Typography>
        </Grid>

        {/* Direcciones */}
        <Grid item xs={12}>
          <Typography variant="h6" fontWeight="bold">
            Direcciones
          </Typography>
          <EnhancedTable
            data={data.identificationDetails.addresses}
            dataHeaders={[
              { key: 'StreetAndNumber', label: 'Dirección' },
              { key: 'AditionalInfo', label: 'Info Adicional' },
              { key: 'Communes', label: 'Comuna' },
              { key: 'Region', label: 'Región' },
              { key: 'AddressDate', label: 'Fecha registro', type: 'date' },
            ]}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h6" fontWeight="bold">
            Números de Teléfono
          </Typography>
          <EnhancedTable
            data={data.identificationDetails.phones}
            dataHeaders={[
              { key: 'Telephone', label: 'Número' },
              { key: 'SourceOfTelephone', label: 'Fuente' },
              { key: 'TelephoneDate', label: 'Fecha registro', type: 'date' },
            ]}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h6" fontWeight="bold">
            Sociedades
          </Typography>
          <EnhancedTable
            data={data.patrimony.societies}
            dataHeaders={[
              { key: 'SocietiyRUT', label: 'Rut' },
              { key: 'descSociety', label: 'Descripción' },
            ]}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h6" fontWeight="bold">
            Actividades
          </Typography>
          <EnhancedTable
            data={[
              {
                economicCode: data.tributary.economicCode,
                economicActivity: data.tributary.economicActivity,
              },
            ]}
            dataHeaders={[
              { key: 'economicCode', label: 'Código' },
              { key: 'economicActivity', label: 'Actividad Económica' },
            ]}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h6" fontWeight="bold">
            Bancos
          </Typography>
          <EnhancedTable
            data={data.patrimony.bankAccounts}
            dataHeaders={[{ key: 'bank_name', label: 'Banco' }]}
          />
        </Grid>

        {/* Score Histórico */}
        {/* <Grid item xs={12}>
          <Typography variant="h6" fontWeight="bold">
            Score Histórico
          </Typography>
          <EnhancedTable
            data={data.buroScores}
            dataHeaders={[
              { key: 'Period', label: 'Periodo' },
              { key: 'Rate', label: 'Score' },
            ]}
          />
        </Grid> */}

        {/* Boletines */}
        <Grid item xs={12}>
          <Typography variant="h6" fontWeight="bold">
            Boletines
          </Typography>
          {boletines.map((boletin, idx) => (
            <Grid item xs={12} key={idx} style={{ marginBottom: '16px' }}>
              <Typography variant="subtitle1" gutterBottom>
                {boletinesLabels[boletin.key]?.label}
              </Typography>
              <Typography>
                <strong>Monto Total:</strong> {formatMoney(boletin.data.amount)}
              </Typography>
              <Typography>
                <strong>Documentos:</strong> {boletin.data.documentCount}
              </Typography>
              {boletin.data.documents?.length > 0 ? (
                <EnhancedTable
                  data={boletin.data.documents}
                  dataHeaders={
                    // Aquí podrías usar boletinesLabels[boletin.key].dataHeaders si usas los mismos headers que para empresas
                    boletinesLabels[boletin.key]?.dataHeaders || []
                  }
                />
              ) : (
                <Typography variant="body2">
                  Sin detalles disponibles.
                </Typography>
              )}
            </Grid>
          ))}
        </Grid>

        {/* Consultas */}
        <Grid item xs={12}>
          <Typography variant="h6" fontWeight="bold">
            Consultas Recientes (6 Meses)
          </Typography>
          <EnhancedTable
            data={data.consults.historic}
            dataHeaders={[
              { key: 'date', label: 'Fecha', type: 'date' },
              { key: 'businessName', label: 'Empresa' },
              { key: 'report', label: 'Tipo de Reporte' },
            ]}
          />
        </Grid>
      </Grid>
    </div>
  );
};

export default PersonReport;
